import {naturalSort} from '@benchling/js-utils';

import {assert} from 'Asserts';
import {Request} from 'requests/RequestConstants';

import {RequestFulfillmentDefinition} from './FulfillmentConstants';

function compareRequests(request1: Request, request2: Request): 0 | 1 | -1 {
  if (request1.id === request2.id) {
    return 0;
  }

  const sortedHumanIds = naturalSort([request1.name, request2.name]);
  if (sortedHumanIds[0] === request1.name) {
    return -1;
  } else {
    assert(sortedHumanIds[0] === request2.name, 'Unexpected sort result');
    return 1;
  }
}

/**
 * Compare IDs by their order in a list, prioritizing null IDs over non-null ones.
 *
 * @param {array} orderedIds - list of ids in order
 * @param {String} id1
 * @param {String} id2
 */
function compareOrderedIdsNullsFirst(
  orderedIds: Array<string>,
  id1: string | null,
  id2: string | null
): number {
  if (id1 === id2) {
    return 0;
  }

  if (id1 == null) {
    assert(id2 != null, 'Cannot compare null ids');
    return -1;
  }
  if (id2 == null) {
    assert(id1 != null, 'Cannot compare null ids');
    return 1;
  }

  const index1 = orderedIds.indexOf(id1);
  const index2 = orderedIds.indexOf(id2);
  if (index1 === -1 || index2 === -1) {
    throw new Error('ID not found - have all models been fetched?');
  }
  return index1 - index2;
}

function compareFulfillments(
  fulfillment1: RequestFulfillmentDefinition,
  fulfillment2: RequestFulfillmentDefinition,
  {
    requestsById,
    requestTaskIdsByRequestId,
    sampleGroupIdsByRequestId,
  }: {
    requestsById: {[id: string]: Request};
    requestTaskIdsByRequestId: {[requestId: string]: Array<string>};
    sampleGroupIdsByRequestId: {[requestId: string]: Array<string>};
  }
): number {
  // Request
  assert(
    requestsById[fulfillment1.requestId] != null && requestsById[fulfillment2.requestId] != null,
    'Request not found - have all requests been fetched?'
  );
  const requestComparison = compareRequests(
    requestsById[fulfillment1.requestId],
    requestsById[fulfillment2.requestId]
  );
  if (requestComparison !== 0) {
    return requestComparison;
  }

  assert(fulfillment1.requestId === fulfillment2.requestId, 'Expected to return request comparison'); // requestComparison === 0
  const requestId = fulfillment1.requestId;

  // Task
  let taskComparison;
  try {
    taskComparison = compareOrderedIdsNullsFirst(
      requestTaskIdsByRequestId[requestId],
      fulfillment1.requestTaskId,
      fulfillment2.requestTaskId
    );
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    assert(false, e.message, {
      requestTaskIdsByRequestId,
      requestId,
      fulfillment1,
      fulfillment2,
    });
  }

  if (taskComparison !== 0) {
    return taskComparison;
  }

  // Sample group
  const sampleGroupComparison = compareOrderedIdsNullsFirst(
    sampleGroupIdsByRequestId[requestId],
    fulfillment1.sampleGroupId,
    fulfillment2.sampleGroupId
  );
  return sampleGroupComparison;
}

/**
 * Sort request fulfillments by comparing (request, task, sample group), nulls first.
 *
 * @param {array} - fulfillments to sort
 * @param {Object} - models from the Redux store
 *                   (requestsById, requestTaskIdsByRequestId, and sampleGroupIdsByRequestId)
 */
export default function sortFulfillments(
  fulfillments: Array<RequestFulfillmentDefinition>,
  {
    requestsById,
    requestTaskIdsByRequestId,
    sampleGroupIdsByRequestId,
  }: {
    requestsById: {[id: string]: Request};
    requestTaskIdsByRequestId: {[requestId: string]: Array<string>};
    sampleGroupIdsByRequestId: {[requestId: string]: Array<string>};
  }
): Array<RequestFulfillmentDefinition> {
  return fulfillments.slice().sort((fulfillment1, fulfillment2) =>
    compareFulfillments(fulfillment1, fulfillment2, {
      requestsById,
      requestTaskIdsByRequestId,
      sampleGroupIdsByRequestId,
    })
  );
}
